import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from '../../utils/convergence'
import { isNullOrUndefined } from '@afterpay/utils'
import styles from './styles.module.css'
import {
  togglePPAModal,
  useAppDispatch,
  useCheckoutState,
  useCheckoutToken,
  useGetPPAModalState,
  useIsApplePaylater,
  useLazyGetCheckoutPpaDocumentQuery,
  useLazyGetCheckoutPpaDocumentPDFQuery,
} from '../../state'
import { Button, Icon, Loader, Modal, Spinner, useDesignLanguagePerClass, Flex } from '@afterpaytouch/core'
import { PPAIframe } from './PPAIframe'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { isInAppBrowser } from '../../utils/device'
import { downloadOrSharePdf } from '../../utils/downloadPdf'
import { useFlag } from '@afterpaytouch/feature'
import { Features } from '../../model/features'

export const PPAModal: FC = () => {
  const [getPpaDocument, { data: ppaDocumentData, isError: ppaDocIsError, isLoading: lazyPpaDocumentIsLoading }] = useLazyGetCheckoutPpaDocumentQuery()
  const [getPpaDocumentPDF, { isLoading: isPDFLoading }] = useLazyGetCheckoutPpaDocumentPDFQuery()
  const { t } = useTranslation()

  const [isPpaError, setIsPpaError] = useState<boolean>(false)
  const { open, isPaymentAuth } = useGetPPAModalState()
  const { started } = useCheckoutState()
  const docId = started?.purchasePaymentAgreementDocumentId
  const token = useCheckoutToken()
  const dispatch = useAppDispatch()
  const designLanguageClass = useDesignLanguagePerClass(styles)
  const isApplePaylater = useIsApplePaylater()
  const isDownloadPPAEnabled = useFlag(Features.CHECKOUT_ENABLE_DOWNLOAD_PPA_PDF_API)
  const isInAppContext = isInAppBrowser() || isApplePaylater

  const onModalDropbackClick = (): void => {
    dispatch(togglePPAModal())
  }

  const printPPA = (): void => {
    const PPAIframe: HTMLIFrameElement | null = document.querySelector('#ppa-list')
    if (isNullOrUndefined(PPAIframe) || isNullOrUndefined(PPAIframe.contentWindow)) {
      if (isNullOrUndefined(PPAIframe)) {
        capture.warning('ppa iframe empty')
      } else {
        capture.warning('ppa iframe content window empty')
      }
      return
    }
    try {
      PPAIframe.contentWindow?.print()
    } catch (error) {
      capture.error('ppa print error', { ...error })
    }
  }

  const downloadPPA = async (): Promise<void> => {
    if (isNullOrUndefined(docId)) {
      return
    }

    try {
      const result = await getPpaDocumentPDF({ token, docId }).unwrap()
      const blob = new Blob([result], { type: 'application/pdf' })
      await downloadOrSharePdf(blob, 'payment-agreement.pdf')
    } catch (error) {
      capture.error('ppa download error', { ...error })
    }
  }

  useEffect(() => {
    if (open) {
      if (isNullOrUndefined(docId)) {
        return
      }
      getPpaDocument({ token, docId })
    }
  }, [open, docId, token])

  useEffect(() => {
    if (ppaDocIsError) {
      setIsPpaError(true)
    }
  }, [ppaDocIsError, open])

  return (
    <Modal
      show={open}
      onBackdropClick={onModalDropbackClick}
      showCloseButton={true}
      testNameSpace='ppa-modal'
      renderMode={isApplePaylater ? 'Native' : undefined}
    >
      <Flex fullWidth justifyContent='space-between' className={designLanguageClass.iframeModalBtn}>
        {isInAppContext && isDownloadPPAEnabled ? (
          <Button.Link type='button' aria-label={t('summary:terms:PPA:download')} onClick={downloadPPA} disabled={isPDFLoading}>
            {isPDFLoading ? <Spinner size='24' /> : <Icon.Download size='Medium' color='Black' />}
          </Button.Link>
        ) : (
          !isInAppContext && (
            <Button.Link type='button' aria-label={t('summary:terms:PPA:print')} onClick={printPPA} disabled={lazyPpaDocumentIsLoading}>
              <Icon.Printer size='Medium' color='Black' />
            </Button.Link>
          )
        )}
        <Button.Link type='button' aria-label={t('summary:terms:PPA:close')} onClick={onModalDropbackClick}>
          <Icon.Close size='Medium' color='Black' />
        </Button.Link>
      </Flex>
      {lazyPpaDocumentIsLoading ? (
        <Loader />
      ) : (
        <PPAIframe ppa={ppaDocumentData?.renderedHtml} isPaymentAuth={isPaymentAuth} isPpaError={isPpaError} isInAppBrowser={isInAppContext} />
      )}
    </Modal>
  )
}
